import { type Lang } from "@/lib/i18n";
import { type SubPageContent } from "@/components/common/SubPageLayout";

export interface FlatPanelPageCopy {
  diagramTitle: string;
  diagramLead: string;
  content: SubPageContent;
}

export const COPY: Record<Lang, FlatPanelPageCopy> = {
  en: {
    diagramTitle: "Planar Resistance-Welded Structure",
    diagramLead: "Continuous surface profile V-wires resistance-welded perpendicularly across rigid support rods create smooth screening decks with non-clogging slot geometry.",
    content: {
      breadcrumbKey: "FlatPanelScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Wedge Wire Screen Deck",
        standardBadge: "Resistance Welded",
        heading: "Flat Panel Wedge Wire Screens",
        lead: "Engineered stainless steel flat screen panels constructed with continuous parallel V-shaped profile wires welded orthogonally across structural support bars. Designed for maximum vibration resistance, high planar stiffness, and uniform dewatering and solids recovery.",
        secondaryCta: {
          label: "View Wedge Wire Screens",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
          alt: "Stainless steel flat panel wedge wire screen with welded perimeter support",
        },
        cardNote: "Planar resistance-welded screen deck",
        cardLink: {
          label: "Parent Overview",
          to: "/WedgeWireScreenPage",
        },
        specs: [
          { label: "Slot Width", value: "0.02 – 5.0 mm" },
          { label: "Construction", value: "Planar Resistance-Welded" },
          { label: "Standard Sizes", value: "Up to 1,500 × 3,000 mm" },
          { label: "Alloy Grades", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Deck Engineering & Mechanical Rigidity",
        heading: "Precision Planar Screening for Heavy Industrial Dewatering",
        paragraphs: [
          "MutualFilter flat panel wedge wire screens are manufactured by fusion-welding parallel surface V-wires at each intersection across transverse structural support rods. This orthogonal matrix produces an exceptionally rigid, flat deck capable of withstanding heavy static bed depths, rapid vibratory accelerations, and high impact from bulk material feeds.",
          "The surface profile wires feature a precision-tapered cross-section that forms continuous, inward-widening slot openings. Fine particles matching or smaller than the slot aperture pass freely without wedging, while oversize solids discharge across the flat surface, preventing blinding and sustaining uninterrupted separation throughput.",
          "Custom perimeter edge options, stiffening gussets, and intermediate cross-ties are configured to match specific vibrating screen decks, static flumes, centrifuge baskets, and drain troughs. Panels can be fabricated with welded flat-bar borders, formed side angles, clamping rebates, or elastomer sealing profiles.",
        ],
        keyPoints: [
          "Orthogonal profile wire construction maintains precise slot openings under heavy gravitational and vibrational loads.",
          "V-shaped wire profile widens inwardly to facilitate rapid liquid release and prevent particle blinding or lodging.",
          "Custom perimeter frame options include welded flat bars, angled flanges, rubber gaskets, and side clamping channels.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
          alt: "Manufacturing and assembly of stainless steel wedge wire screen panels",
        },
      },
      specTable: {
        heading: "Standard Flat Panel Manufacturing Parameters",
        intro: "Representative slot apertures, profile wire geometry, support rod spacing, and typical application sectors for flat wedge wire panels.",
        columns: [
          { key: "slot", label: "Slot Aperture (mm)", align: "left" },
          { key: "surfaceWire", label: "Surface Wire Profile", align: "left" },
          { key: "supportRod", label: "Support Rod Type", align: "left" },
          { key: "rodPitch", label: "Support Spacing", align: "right" },
          { key: "primaryDuty", label: "Typical Process Duty", align: "left" },
        ],
        rows: [
          {
            slot: "0.05 – 0.20",
            surfaceWire: "Narrow V-wire",
            supportRod: "Flat bar / rectangular rod",
            rodPitch: "15 – 25 mm",
            primaryDuty: "Fine mineral dewatering & starch extraction",
          },
          {
            slot: "0.25 – 0.50",
            surfaceWire: "Standard industrial V-wire",
            supportRod: "High-depth flat bar",
            rodPitch: "20 – 30 mm",
            primaryDuty: "Coal preparation, pulp thickening, wastewater",
          },
          {
            slot: "0.75 – 1.50",
            surfaceWire: "Heavy-duty V-wire",
            supportRod: "Reinforced structural bar",
            rodPitch: "25 – 40 mm",
            primaryDuty: "Aggregate sizing, sugar processing, intake beds",
          },
          {
            slot: "2.00 – 5.00",
            surfaceWire: "Max-cross section V-wire",
            supportRod: "Heavy orthogonal carrier bar",
            rodPitch: "30 – 50 mm",
            primaryDuty: "Coarse solids classification, trash screening",
          },
        ],
        caption: "Available in AISI 304 and 316L. Custom slot widths between 0.02 mm and 5.0 mm and specialized framing profiles fabricated on request.",
      },
      features: {
        heading: "Key Engineering Attributes",
        intro: "Structural advantages of resistance-welded flat panel decks over conventional wire cloth and perforated plates.",
        items: [
          {
            title: "Anti-Blinding V-Profile",
            text: "Continuous narrow two-point contact line widening inward minimizes friction and prevents particle wedging or buildup during continuous slurry feeding.",
          },
          {
            title: "Planar Vibration Stability",
            text: "Full resistance welding at every intersection produces rigid, non-loosening panels that resist cyclic fatigue on high-G vibrating screens.",
          },
          {
            title: "High Open Area Efficiency",
            text: "Parallel wire arrangement optimizes fluid discharge rates compared to perforated metal sheets of equivalent retention ratings.",
          },
          {
            title: "Versatile Border Mounts",
            text: "Available with welded framing flat bars, bolt-hole flanges, side tension hooks, or rubber side dampers for direct deck drop-in installation.",
          },
        ],
      },
      related: {
        heading: "Related Wedge Wire Screen Configurations",
        intro: "Explore complementary wedge wire geometry formats designed for curved, cylindrical, tubular, and nozzle-based filtration systems.",
        cards: [
          {
            title: "Wedge Wire Screen",
            text: "Comprehensive family overview of all resistance-welded V-wire profiles, slot orientations, and industrial separation formats.",
            to: "/WedgeWireScreenPage",
            badge: "Product Family",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
              alt: "Industrial wedge wire screen overview",
            },
          },
          {
            title: "Curved Screens",
            text: "Curved sieve and radius-formed screen decks utilizing gravity feed acceleration and tangential hydraulic peeling.",
            to: "/CurvedScreensPage",
            badge: "Radial Formats",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
              alt: "Curved wedge wire screen panel",
            },
          },
          {
            title: "Cylindrical Screens",
            text: "Rotary and internal/external flow cylindrical wedge wire screens with axial or circumferential slot orientations.",
            to: "/CylindricalScreensPage",
            badge: "Cylinders",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Cylindrical wedge wire screens",
            },
          },
          {
            title: "Screen Tubes",
            text: "Reinforced wedge wire pipes and tube assemblies for deep well intake, resin retention, and high-pressure cartridge strainers.",
            to: "/ScreenTubesPage",
            badge: "Tubular",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
              alt: "Wedge wire screen tubes and pipes",
            },
          },
          {
            title: "Sieve Bend Screens",
            text: "Precision gravity run-down screens with cross-flow dewatering profiles for starch, coal slurries, and pulp processing.",
            to: "/SieveBendScreensPage",
            badge: "Gravity Screens",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
              alt: "Stainless steel sieve bend screen",
            },
          },
          {
            title: "Filter Nozzles",
            text: "Threaded and flanged wedge wire nozzle strainers for ion exchange vessels, sand filters, and water treatment underdrains.",
            to: "/FilterNozzlesPage",
            badge: "Underdrain",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
              alt: "Stainless steel wedge wire filter nozzles",
            },
          },
        ],
      },
      cta: {
        id: "flat-panel-cta",
        eyebrow: "Engineered Screen Panels",
        heading: "Request a Technical Quote for Flat Panel Screens",
        lead: "Submit your deck dimensions, required slot width, support bar profile, and operating requirements for an itemized specification and quote.",
        secondaryLabel: "Explore Wedge Wire Family",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Available in AISI 304 and 316L stainless steel alloys with custom mounting frames.",
      },
    },
  },
  zh: {
    diagramTitle: "平面全熔阻焊结构原理",
    diagramLead: "连续平行的 V 型梯形表面丝条与垂直方向的坚固支撑筋条经电阻焊紧密熔接，形成平整光滑且具备倒锥防堵缝隙的工业筛板。",
    content: {
      breadcrumbKey: "FlatPanelScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "楔形丝筛网板",
        standardBadge: "全熔电阻焊",
        heading: "不锈钢平板楔形丝筛板",
        lead: "采用连续平行 V 型截面楔形绕丝与高刚度支撑筋条精密正交电阻焊接而成的不锈钢平板筛板。专为抵抗机械振动冲击、承受高重力物料载荷并在高负荷工业分选与脱水流程中保持平整度与精确缝隙而设计。",
        secondaryCta: {
          label: "查看楔形丝产品总览",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
          alt: "带加强边框的不锈钢平板丝网筛板",
        },
        cardNote: "高刚度全熔阻焊平面筛面",
        cardLink: {
          label: "产品大类",
          to: "/WedgeWireScreenPage",
        },
        specs: [
          { label: "缝隙宽度", value: "0.02 – 5.0 mm" },
          { label: "制造结构", value: "平板全熔阻焊结构" },
          { label: "标准尺寸", value: "最大 1,500 × 3,000 mm" },
          { label: "标准材质", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "筛板机械强度与刚度结构",
        heading: "高负荷工业脱水与固液分选的刚性平面筛面",
        paragraphs: [
          "MutualFilter 平板楔形丝筛板通过在每处交叉接点将平行布置的表面 V 型丝与横向高刚度支撑筋条进行电阻全熔焊接制成。这种正交刚性网状结构赋予筛面出色的抗形变能力与平面平整度，能够经受大厚度料层重压、高频振动加速度以及散料的高动能冲击。",
          "表面型材丝采用向下逐步展开的倒梯形截面，在筛板正面形成狭窄的两点接触入料口，向背侧则迅速扩张。达到或小于缝隙开孔的微细物料和液体能够顺畅穿透排出，彻底杜绝颗粒在筛缝内部楔入卡堵，维持高效且连续的物料分选能力。",
          "可根据振动筛机架、静态溜槽、离心机底座或沉淀槽的具体安装要求，定制不同形式的外周加强边框、加强筋和密封角钢。支持全焊扁钢边框、冲孔法兰、侧边压紧倒角或弹性聚氨酯包边。",
        ],
        keyPoints: [
          "正交高刚度全熔阻焊结构，在高振动及重度重力载荷下始终保持均一稳定的筛缝开孔精度。",
          "向后扩张的倒 V 型梯形缝隙截面促进液体快速剥离排出，杜绝固体颗粒在筛缝内部卡堵结垢。",
          "支持多样化边框加工，包括全焊扁钢边框、角钢安装法兰、聚氨酯橡胶密封条及侧边压紧型材。",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
          alt: "不锈钢楔形丝筛板生产与制造",
        },
      },
      specTable: {
        heading: "标准平板筛板制造参数参考",
        intro: "涵盖典型缝隙宽度、表面异型丝型号、支撑筋规格及常见适用工况。",
        columns: [
          { key: "slot", label: "缝隙宽度 (mm)", align: "left" },
          { key: "surfaceWire", label: "表面丝条截面", align: "left" },
          { key: "supportRod", label: "支撑筋条类型", align: "left" },
          { key: "rodPitch", label: "支撑筋间距", align: "right" },
          { key: "primaryDuty", label: "典型工业用途", align: "left" },
        ],
        rows: [
          {
            slot: "0.05 – 0.20",
            surfaceWire: "窄截面高精度V型丝",
            supportRod: "矩形扁钢支撑条",
            rodPitch: "15 – 25 mm",
            primaryDuty: "微细矿物脱水与淀粉提取",
          },
          {
            slot: "0.25 – 0.50",
            surfaceWire: "标准工业级V型丝",
            supportRod: "高截面加强扁钢",
            rodPitch: "20 – 30 mm",
            primaryDuty: "选煤脱水、纸浆浓缩、污水沉降",
          },
          {
            slot: "0.75 – 1.50",
            surfaceWire: "重载耐磨V型丝",
            supportRod: "重型结构支撑扁条",
            rodPitch: "25 – 40 mm",
            primaryDuty: "骨料分级、制糖过滤、取水过滤床",
          },
          {
            slot: "2.00 – 5.00",
            surfaceWire: "大截面粗丝V型条",
            supportRod: "重型正交主受力承载梁",
            rodPitch: "30 – 50 mm",
            primaryDuty: "粗颗粒分选、杂质粗筛截留",
          },
        ],
        caption: "提供 AISI 304 与 316L 不锈钢材质。可在 0.02 mm 至 5.0 mm 范围内定制任意缝隙开孔及专用边框结构。",
      },
      features: {
        heading: "主要结构与工艺优势",
        intro: "全熔电阻焊平面筛网相比传统编织金属网和冲孔板的显著工程特点。",
        items: [
          {
            title: "内宽自释放防卡堵截面",
            text: "仅在表面形成窄接触线，向背部扩张的梯形间隙大幅降低流道摩擦，防止固体颗粒在筛缝内卡死。",
          },
          {
            title: "高振动平面稳定性",
            text: "每个交叉焊点实现全熔电阻焊接，整体刚度高，能长期耐受大重力加速度振动筛的交变疲劳。",
          },
          {
            title: "更高的有效开孔率",
            text: "平行排列的连续缝隙结构比同等截留精度的冲孔板提供更大的有效过流截面与更快脱水速度。",
          },
          {
            title: "灵活多样的安装边框",
            text: "可定制全焊外边框扁钢、螺栓固定法兰、侧向张紧勾边或橡胶减震垫条，便捷适配各型号筛机。",
          },
        ],
      },
      related: {
        heading: "相关楔形丝过滤与筛分产品",
        intro: "了解弧形筛、圆柱筛筒、筛管以及滤帽等其他几何结构的楔形丝产品系列。",
        cards: [
          {
            title: "楔形丝筛网总览",
            text: "包含全部电阻焊 V 型型材、开孔方向以及工业分选结构的产品系列总览。",
            to: "/WedgeWireScreenPage",
            badge: "产品大类",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
              alt: "工业楔形丝筛网制造与产品总览",
            },
          },
          {
            title: "弧形筛板",
            text: "利用重力进料切向水力剥离效应的高效脱水与预分选弧形曲面筛网。",
            to: "/CurvedScreensPage",
            badge: "弧形曲面",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
              alt: "不锈钢弧形楔形丝筛板",
            },
          },
          {
            title: "圆柱形筛筒",
            text: "旋转筛机与内/外流过滤装置适用的轴向或圆周向缝隙圆柱形筛筒。",
            to: "/CylindricalScreensPage",
            badge: "圆柱结构",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "圆柱形楔形丝筛网",
            },
          },
          {
            title: "楔形丝筛管",
            text: "用于深井取水、树脂捕捉器以及高压滤芯的高强度支撑骨架与筛管组件。",
            to: "/ScreenTubesPage",
            badge: "管状过滤",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
              alt: "不锈钢楔形丝筛管与滤管",
            },
          },
          {
            title: "重力弧形曲筛",
            text: "淀粉生产、洗煤泥脱水与造纸纸浆微细固液分离的高效重力自流筛板。",
            to: "/SieveBendScreensPage",
            badge: "自流分离",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
              alt: "不锈钢重力弧形筛",
            },
          },
          {
            title: "楔形丝滤帽与喷嘴",
            text: "离子交换柱、石英砂过滤床及水处理底部配水集水系统的精密螺纹与法兰滤帽。",
            to: "/FilterNozzlesPage",
            badge: "底部布水",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
              alt: "不锈钢楔形丝滤帽喷嘴",
            },
          },
        ],
      },
      cta: {
        id: "flat-panel-cta",
        eyebrow: "定制工业筛板",
        heading: "获取不锈钢平板楔形丝筛板工程报价",
        lead: "请提供您的机架尺寸、缝隙开孔要求、支撑条规格及工艺物料工况，我们将为您提供技术评估与规格报价方案。",
        secondaryLabel: "返回楔形丝总览",
        secondaryTo: "/WedgeWireScreenPage",
        note: "标准提供 AISI 304 与 316L 不锈钢材质，支持各类法兰边框定制加工。",
      },
    },
  },
  es: {
    diagramTitle: "Estructura plana electrosoldada por resistencia",
    diagramLead: "Alambres superficiales continuos con perfil en V electrosoldados perpendicularmente sobre varillas de soporte rígidas, formando paneles planos de gran rigidez y ranura antiobstrucción.",
    content: {
      breadcrumbKey: "FlatPanelScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Placas de criba en cuña",
        standardBadge: "Electrosoldadura plana",
        heading: "Paneles planos de alambre en cuña",
        lead: "Paneles de criba planos de acero inoxidable fabricados con alambres continuos en perfil V soldados ortogonalmente sobre barras de soporte estructurales. Diseñados para una máxima resistencia a la vibración, alta rigidez planar y deshidratación uniforme de pulpas y minerales.",
        secondaryCta: {
          label: "Ver cribas de alambre en cuña",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
          alt: "Panel plano de alambre en cuña de acero inoxidable con marco de soporte",
        },
        cardNote: "Estructura plana electrosoldada de alta rigidez",
        cardLink: {
          label: "Resumen de cribas",
          to: "/WedgeWireScreenPage",
        },
        specs: [
          { label: "Ancho de ranura", value: "0,02 – 5,0 mm" },
          { label: "Construcción", value: "Plana electrosoldada" },
          { label: "Dimensiones estándar", value: "Hasta 1.500 × 3.000 mm" },
          { label: "Aleaciones", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Rigidez mecánica y soporte estructural",
        heading: "Superficies planas de alta precisión para deshidratación y clasificación",
        paragraphs: [
          "Los paneles planos de alambre en cuña MutualFilter se fabrican electrosoldando por fusión alambres continuos en V en cada punto de cruce sobre barras de soporte transversales. Esta disposición ortogonal produce un conjunto de elevada solidez capaz de resistir cargas estáticas pesadas, aceleraciones vibratorias continuas e impactos directos de material a granel.",
          "El perfil de alambre presenta una sección trapezoidal que forma ranuras continuas ensanchadas hacia el interior. Las partículas de tamaño inferior o igual a la abertura atraviesan libremente sin atascarse, mientras que los sólidos retenidos se evacuan sobre la cara superior, evitando el colmatado mecánico y asegurando un caudal constante.",
          "Las dimensiones perimetrales, marcos laterales y refuerzos intermedios se adaptan a los lechos de cribas vibratorias, canaletas estáticas, centrifugadoras y tolvas de drenaje. Los paneles pueden suministrarse con marco perimetral soldado, perfiles en ángulo, orificios para pernos o juntas amortiguadoras de caucho.",
        ],
        keyPoints: [
          "Estructura ortogonal electrosoldada que mantiene la abertura de ranura uniforme bajo altas cargas gravitatorias y vibratorias.",
          "El perfil de alambre en cuña que se ensancha hacia el interior previene el colmatado mecánico y facilita el flujo libre.",
          "Opciones de marco perimetral soldado, bridas en ángulo, juntas elastoméricas y perfiles de sujeción lateral.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
          alt: "Fabricación de paneles de criba de alambre en cuña en acero inoxidable",
        },
      },
      specTable: {
        heading: "Parámetros estándar de fabricación de paneles planos",
        intro: "Aberturas de ranura de referencia, geometría de alambres y barras portantes para las aplicaciones industriales habituales.",
        columns: [
          { key: "slot", label: "Abertura de ranura (mm)", align: "left" },
          { key: "surfaceWire", label: "Perfil de alambre superficial", align: "left" },
          { key: "supportRod", label: "Barra de soporte", align: "left" },
          { key: "rodPitch", label: "Paso de soporte", align: "right" },
          { key: "primaryDuty", label: "Aplicación típica", align: "left" },
        ],
        rows: [
          {
            slot: "0,05 – 0,20",
            surfaceWire: "Alambre fino en V",
            supportRod: "Pletina rectangular",
            rodPitch: "15 – 25 mm",
            primaryDuty: "Deshidratación de minerales finos y almidón",
          },
          {
            slot: "0,25 – 0,50",
            surfaceWire: "Alambre estándar en V",
            supportRod: "Pletina de gran canto",
            rodPitch: "20 – 30 mm",
            primaryDuty: "Lavado de carbón, pasta papelera, aguas residuales",
          },
          {
            slot: "0,75 – 1,50",
            surfaceWire: "Alambre reforzado en V",
            supportRod: "Barra estructural reforzada",
            rodPitch: "25 – 40 mm",
            primaryDuty: "Clasificación de áridos, industria azucarera",
          },
          {
            slot: "2,00 – 5,00",
            surfaceWire: "Alambre de gran sección",
            supportRod: "Viga portante de carga pesada",
            rodPitch: "30 – 50 mm",
            primaryDuty: "Clasificación gruesa y desbaste primario",
          },
        ],
        caption: "Fabricación en AISI 304 y 316L. Aberturas de ranura personalizadas entre 0,02 mm y 5,0 mm bajo requerimiento.",
      },
      features: {
        heading: "Ventajas técnicas de los paneles planos",
        intro: "Cualidades distintivas frente a mallas metálicas tejidas convencionales y chapas perforadas.",
        items: [
          {
            title: "Sección V antiobstrucción",
            text: "El contacto tangencial inicial con ensanchamiento hacia el fondo disminuye la resistencia y evita que los granos sólidos se incrusten.",
          },
          {
            title: "Resistencia a la vibración",
            text: "Unión por electrosoldadura continua en cada nodo que garantiza una gran durabilidad en cribas sometidas a elevadas fuerzas G.",
          },
          {
            title: "Área abierta optimizada",
            text: "La continuidad de las ranuras ofrece una capacidad de desagüe superior a la de chapas perforadas de corte equivalente.",
          },
          {
            title: "Marcos adaptables",
            text: "Bordes reforzados con pletina plana, ganchos de tensado, o perfiles con juntas de goma para montaje directo.",
          },
        ],
      },
      related: {
        heading: "Otras configuraciones de alambre en cuña",
        intro: "Conozca los formatos cilíndricos, curvados y tubulares fabricados con la misma tecnología de perfiles soldados.",
        cards: [
          {
            title: "Cribas de alambre en cuña",
            text: "Visión global de todos los perfiles en V, orientaciones de ranura y formatos industriales de separación.",
            to: "/WedgeWireScreenPage",
            badge: "Familia de producto",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
              alt: "Resumen de cribas de alambre en cuña",
            },
          },
          {
            title: "Paneles curvados",
            text: "Cribas en arco y paneles de radio curvado que aprovechan el desprendimiento hidráulico tangencial.",
            to: "/CurvedScreensPage",
            badge: "Formatos radiales",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
              alt: "Panel de criba curvado",
            },
          },
          {
            title: "Cilindros filtrantes",
            text: "Tambores rotativos y cilindros de flujo centrífugo o centrípeto con ranuras axiales o circunferenciales.",
            to: "/CylindricalScreensPage",
            badge: "Cilindros",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Cilindros de alambre en cuña",
            },
          },
          {
            title: "Tubos de criba",
            text: "Tubos filtrantes de alta presión y captación para pozos, lechos de resina y cartuchos de retención.",
            to: "/ScreenTubesPage",
            badge: "Tubulares",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
              alt: "Tubos de criba en acero inoxidable",
            },
          },
          {
            title: "Cribas de arco (Sieve Bend)",
            text: "Paneles estáticos autolimpiantes para deshidratación gravitatoria en almidón, pulpa y carbón.",
            to: "/SieveBendScreensPage",
            badge: "Alimentación por gravedad",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
              alt: "Criba de arco estática",
            },
          },
          {
            title: "Toberas filtrantes",
            text: "Toberas ranuradas roscadas o con brida para falsos fondos de filtración, columnas de resina y filtros de arena.",
            to: "/FilterNozzlesPage",
            badge: "Drenaje de fondo",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
              alt: "Toberas filtrantes de alambre en cuña",
            },
          },
        ],
      },
      cta: {
        id: "flat-panel-cta",
        eyebrow: "Paneles a medida",
        heading: "Solicite cotización para paneles planos de alambre en cuña",
        lead: "Indique las dimensiones del marco, la abertura de ranura requerida y el entorno de operación para recibir una propuesta técnica detallada.",
        secondaryLabel: "Ver familia de cribas",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Disponibles en aceros inoxidables AISI 304 y 316L con marcos perimetrales soldados.",
      },
    },
  },
  de: {
    diagramTitle: "Planare widerstandsgeschweißte Konstruktion",
    diagramLead: "Kontinuierliche V-förmige Oberflächendrähte, die orthogonal auf starren Tragstäben widerstandsverschweißt sind, bilden ebene Siebbeläge mit selbstreinigendem Querschnitt.",
    content: {
      breadcrumbKey: "FlatPanelScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Spaltsiebboden",
        standardBadge: "Widerstandsverschweißt",
        heading: "Flachpaneele aus Spaltsiebdraht",
        lead: "Präzisionsgefertigte Flachsiebplatten aus Edelstahl mit parallelen V-Profildrähten, die orthogonal auf quer verlaufenden Tragleisten verschweißt sind. Konzipiert für maximale Schwingungsfestigkeit, hohe planare Steifigkeit und zuverlässige Feststoffentwässerung.",
        secondaryCta: {
          label: "Spaltsiebe Übersicht",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
          alt: "Edelstahl-Flachsieb mit verstärkter Randeinfassung",
        },
        cardNote: "Ebene widerstandsverschweißte Siebfläche",
        cardLink: {
          label: "Hauptkategorie",
          to: "/WedgeWireScreenPage",
        },
        specs: [
          { label: "Spaltweite", value: "0,02 – 5,0 mm" },
          { label: "Konstruktion", value: "Planar widerstandsverschweißt" },
          { label: "Standardmaße", value: "Bis zu 1.500 × 3.000 mm" },
          { label: "Werkstoffe", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Strukturelle Steifigkeit und Tragfähigkeit",
        heading: "Präzisions-Flachpaneele für industrielle Entwässerung und Klassierung",
        paragraphs: [
          "MutualFilter Flachpaneele aus Spaltsiebdraht entstehen durch thermisches Widerstandsschweißen der parallelen V-Profildrähte an jedem Schnittpunkt mit den quer laufenden Tragstäben. Dieser feste Verbund gewährleistet eine außergewöhnliche Verwindungssteifigkeit, die auch hohen Schüttgutgewichten, Vibrationsbeschleunigungen und Stoßbelastungen standhält.",
          "Die keilförmigen Profildrähte verjüngen sich nach innen und bilden kontinuierliche, sich nach hinten öffnende Spaltöffnungen. Teilchen, die kleiner als die Spaltweite sind, passieren das Sieb ungehindert, während Überkorn über die ebene Oberfläche ausgetragen wird, was Verstopfungen nachhaltig unterbindet.",
          "Randabschlüsse, Versteifungsrippen und Befestigungswinkel werden präzise an die jeweiligen Vibrationssiebmaschinen, Rinnen, Zentrifugenböden oder Gerinne angepasst. Die Fertigung erfolgt wahlweise mit aufgeschweißten Flachstahlleisten, Winkelflanschen oder Gummiprofildichtungen.",
        ],
        keyPoints: [
          "Orthogonale Schweißkonstruktion gewährleistet dauerhaft präzise Spaltweiten auch unter extremen Vibrations- und Schwerkraftlasten.",
          "Sich nach hinten öffnender V-Draht-Querschnitt verhindert Verstopfen und gewährleistet kontinuierliche Selbstreinigung.",
          "Rahmeneinfassungen nach Maß mit Flachprofilen, Winkelflanschen, Dichtungsauflagen und seitlichen Klemmleisten.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
          alt: "Fertigung von Spaltsieb-Flachpaneelen aus Edelstahl",
        },
      },
      specTable: {
        heading: "Standard-Fertigungsparameter für Flachpaneele",
        intro: "Referenzwerte für Spaltweiten, Drahtgeometrien und Tragstababstände für industrielle Trennprozesse.",
        columns: [
          { key: "slot", label: "Spaltweite (mm)", align: "left" },
          { key: "surfaceWire", label: "Oberflächendraht-Profil", align: "left" },
          { key: "supportRod", label: "Tragstab-Ausführung", align: "left" },
          { key: "rodPitch", label: "Tragstababstand", align: "right" },
          { key: "primaryDuty", label: "Typischer Einsatzbereich", align: "left" },
        ],
        rows: [
          {
            slot: "0,05 – 0,20",
            surfaceWire: "Feiner V-Profildraht",
            supportRod: "Rechteckige Flachleiste",
            rodPitch: "15 – 25 mm",
            primaryDuty: "Feinstentwässerung von Mineralien & Stärke",
          },
          {
            slot: "0,25 – 0,50",
            surfaceWire: "Standard V-Profildraht",
            supportRod: "Hohe Rechteckleiste",
            rodPitch: "20 – 30 mm",
            primaryDuty: "Kohleaufbereitung, Zellstoffentwässerung",
          },
          {
            slot: "0,75 – 1,50",
            surfaceWire: "Robuster V-Profildraht",
            supportRod: "Verstärkte Tragstruktur",
            rodPitch: "25 – 40 mm",
            primaryDuty: "Klassierung von Schüttgütern, Zuckerindustrie",
          },
          {
            slot: "2,00 – 5,00",
            surfaceWire: "Großprofil-V-Draht",
            supportRod: "Schwere Tragstabbalken",
            rodPitch: "30 – 50 mm",
            primaryDuty: "Grobtrennung und Grobstoffrückhalt",
          },
        ],
        caption: "Lieferbar in AISI 304 und 316L. Individuelle Spaltweiten von 0,02 mm bis 5,0 mm auf Anfrage realisierbar.",
      },
      features: {
        heading: "Konstruktive Leistungsmerkmale",
        intro: "Wesentliche Vorteile der widerstandsverschweißten Paneele gegenüber Drahtgeweben und Lochblechen.",
        items: [
          {
            title: "Verstopfungsfreies V-Profil",
            text: "Zweipunktkontakt an der Oberfläche mit sich nach unten erweiterndem Querschnitt verhindert das Einklemmen von Feststoffen.",
          },
          {
            title: "Schwingungsstabilität",
            text: "Vollverschweißte Knotenpunkte verleihen dem Siebbelag hohe Dauerwechselfestigkeit auf Schwingmaschinen.",
          },
          {
            title: "Günstiges Durchflussverhalten",
            text: "Parallele Spalte erzeugen eine hohe freie Siebfläche und beschleunigen die Fest-Flüssig-Trennung.",
          },
          {
            title: "Anpassbare Einfassungen",
            text: "Umlaufende Flachstähle, Befestigungsflansche oder Spannfalze zur passgenauen Montage in vorhandene Rahmen.",
          },
        ],
      },
      related: {
        heading: "Weitere Spaltsieb-Ausführungen",
        intro: "Erfahren Sie mehr über gebogene Siebe, Zylinder und Filterrohre aus widerstandsverschweißten Profildrähten.",
        cards: [
          {
            title: "Spaltsiebe Übersicht",
            text: "Gesamtübersicht aller V-Draht-Profile, Spaltausrichtungen und industriellen Siebkonstruktionen.",
            to: "/WedgeWireScreenPage",
            badge: "Produktfamilie",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
              alt: "Übersicht industrielle Spaltsiebe",
            },
          },
          {
            title: "Gebogene Siebe",
            text: "Radiusgeformte Bogensiebe für hydrodynamische Schwerkraftabscheidung und tangentiale Feststofftrennung.",
            to: "/CurvedScreensPage",
            badge: "Radiale Formate",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
              alt: "Gebogenes Spaltsiebpaneel",
            },
          },
          {
            title: "Zylindersiebe",
            text: "Rotations- und Durchströmtrommeln mit axialer oder zirkulärer Spaltausrichtung für Zentrifugen und Trommelfilter.",
            to: "/CylindricalScreensPage",
            badge: "Zylinder",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Zylindrische Spaltsiebe",
            },
          },
          {
            title: "Siebrohre",
            text: "Druckfeste Spaltsiebrohre für Tiefbrunnen, Harzfänger und industrielle Filterkerzen.",
            to: "/ScreenTubesPage",
            badge: "Rohre",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
              alt: "Edelstahl-Spaltsiebrohre",
            },
          },
          {
            title: "Bogensiebböden",
            text: "Statische Schwerkraft-Bogensiebe für die Stärkegewinnung, Zellstoffentwässerung und Kohleaufbereitung.",
            to: "/SieveBendScreensPage",
            badge: "Schwerkraftsiebe",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
              alt: "Statisches Bogensieb",
            },
          },
          {
            title: "Filterdüsen",
            text: "Spaltsiebdüsen mit Gewinde- oder Flanschanschluss für Ionenaustauscher, Sandfilter und Filterböden.",
            to: "/FilterNozzlesPage",
            badge: "Düsenböden",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
              alt: "Filterdüsen aus Edelstahl-Spaltsiebdraht",
            },
          },
        ],
      },
      cta: {
        id: "flat-panel-cta",
        eyebrow: "Maßgefertigte Siebplatten",
        heading: "Technisches Angebot für Flachpaneele anfordern",
        lead: "Senden Sie uns Ihre Einbaumaße, die gewünschte Spaltweite sowie die Tragstabgeometrie für ein unverbindliches Angebot.",
        secondaryLabel: "Spaltsiebe erkunden",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Gefertigt in Edelstahl AISI 304 und 316L mit individuellen Randeinfassungen.",
      },
    },
  },
  fr: {
    diagramTitle: "Structure plane mécano-soudée par résistance",
    diagramLead: "Les fils continus en profil V sont soudés par résistance perpendiculairement sur des barres de support rigides pour former un plan de criblage indéformable et autonettoyant.",
    content: {
      breadcrumbKey: "FlatPanelScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Plancher tamisant en fil profilé",
        standardBadge: "Soudure par résistance",
        heading: "Panneaux filtrants plats en fil profilé",
        lead: "Panneaux plats en acier inoxydable constitués de fils continus en V soudés orthogonalement sur des barres supports transversales rigides. Conçus pour une résistance maximale aux vibrations, une excellente planéité et un égouttage uniforme des boues et des solides.",
        secondaryCta: {
          label: "Découvrir la grille en fil profilé",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
          alt: "Panneau filtrant plat en fil profilé inox avec cadre renforcé",
        },
        cardNote: "Plancher plan mécano-soudé haute rigidité",
        cardLink: {
          label: "Famille de produits",
          to: "/WedgeWireScreenPage",
        },
        specs: [
          { label: "Largeur de fente", value: "0,02 – 5,0 mm" },
          { label: "Construction", value: "Plane mécano-soudée" },
          { label: "Dimensions standard", value: "Jusqu'à 1 500 × 3 000 mm" },
          { label: "Nuances d'alliage", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Rigidité structurelle et tenue mécanique",
        heading: "Surfaces planes de haute précision pour essorage et séparation industrielle",
        paragraphs: [
          "Les panneaux plats en fil profilé MutualFilter sont élaborés par soudage électrique par résistance à chaque intersection entre les fils de surface en V et les barres supports transversales. Ce maillage orthogonal confère une résistance remarquable capable de supporter de lourdes charges de matière, de fortes accélérations vibratoires et des chocs récurrents.",
          "Le profil en V des fils forme une ouverture continue qui s'élargit progressivement vers le bas. Les particules dont la granulométrie est inférieure ou égale à la fente s'évacuent instantanément sans colmatage interne, tandis que la fraction retenue s'écoule à la surface du panneau.",
          "Les rives périphériques, raidisseurs et encadrements sont configurés selon les exigences de vos tamis vibrants, goulottes, essoreuses centrifuges et caniveaux de drainage. Les panneaux peuvent être équipés de fers plats soudés, de cornières de fixation ou de bandes d'étanchéité élastomères.",
        ],
        keyPoints: [
          "Rigidité orthogonale exceptionnelle préservant la précision de la fente sous fortes contraintes mécaniques et vibratoires.",
          "Profil en V s'évasant vers l'arrière pour éliminer les risques de colmatage et accélérer l'évacuation des liquides.",
          "Cadres perimétriques personnalisés avec fers plats soudés, cornières de fixation et profilés de serrage latéral.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
          alt: "Fabrication de panneaux tamisants en acier inoxydable",
        },
      },
      specTable: {
        heading: "Paramètres standard de fabrication des panneaux plats",
        intro: "Aperçu des ouvertures de fentes usuelles, profils de fils de surface et espacements des supports transversaux.",
        columns: [
          { key: "slot", label: "Ouverture de fente (mm)", align: "left" },
          { key: "surfaceWire", label: "Profil du fil de surface", align: "left" },
          { key: "supportRod", label: "Barre de support", align: "left" },
          { key: "rodPitch", label: "Entraxe des supports", align: "right" },
          { key: "primaryDuty", label: "Application industrielle", align: "left" },
        ],
        rows: [
          {
            slot: "0,05 – 0,20",
            surfaceWire: "Fil fin en V",
            supportRod: "Barre rectangulaire",
            rodPitch: "15 – 25 mm",
            primaryDuty: "Essorage de minéraux fins et amidon",
          },
          {
            slot: "0,25 – 0,50",
            surfaceWire: "Fil standard en V",
            supportRod: "Barre haute section",
            rodPitch: "20 – 30 mm",
            primaryDuty: "Lavage du charbon, pâte à papier, eaux usées",
          },
          {
            slot: "0,75 – 1,50",
            surfaceWire: "Fil lourd en V",
            supportRod: "Barre structurelle renforcée",
            rodPitch: "25 – 40 mm",
            primaryDuty: "Calibrage d'agrégats, sucrerie",
          },
          {
            slot: "2,00 – 5,00",
            surfaceWire: "Fil de forte section en V",
            supportRod: "Poutrelle porteuse lourde",
            rodPitch: "30 – 50 mm",
            primaryDuty: "Criblage grossier et dégrillage primaire",
          },
        ],
        caption: "Disponible en nuances AISI 304 et 316L. Fentes sur mesure de 0,02 mm à 5,0 mm configurables sur demande.",
      },
      features: {
        heading: "Avantages de conception mécanique",
        intro: "Points forts face aux toiles métalliques tissées classiques et aux tôles perforées.",
        items: [
          {
            title: "Profil anti-colmatage en V",
            text: "Contact en arête avec élargissement interne réduisant le frottement et empêchant le blocage des particules solides.",
          },
          {
            title: "Stabilité sous vibrations",
            text: "Soudure continue à chaque intersection assurant une haute résistance à la fatigue sur tamiseurs vibrants.",
          },
          {
            title: "Surface ouverte maximale",
            text: "L'alignement continu des fentes autorise un débit de filtration plus élevé qu'une tôle perforée équivalente.",
          },
          {
            title: "Fixations sur mesure",
            text: "Bordures usinées, fers plats d'encadrement ou cornières de maintien assurant une pose directe sans jeu.",
          },
        ],
      },
      related: {
        heading: "Configurations complémentaires en fil profilé",
        intro: "Consultez l'ensemble des panneaux courbes, tambours rotatifs et paniers tubulaires de notre gamme.",
        cards: [
          {
            title: "Grilles en fil profilé",
            text: "Vue d'ensemble de la famille complète des profilés en V, orientations de fentes et dispositifs de séparation.",
            to: "/WedgeWireScreenPage",
            badge: "Gamme produit",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
              alt: "Vue d'ensemble grilles en fil profilé",
            },
          },
          {
            title: "Panneaux courbes",
            text: "Tamis arqués utilisant la pesanteur et l'effet d'arrachement tangentiel pour une séparation continue.",
            to: "/CurvedScreensPage",
            badge: "Profils radiaux",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
              alt: "Panneau courbe en fil profilé",
            },
          },
          {
            title: "Cylindres filtrants",
            text: "Trommels et tambours à flux entrant ou sortant avec fentes longitudinales ou circonférentielles.",
            to: "/CylindricalScreensPage",
            badge: "Cylindres",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Cylindres filtrants en fil profilé",
            },
          },
          {
            title: "Tubes et crépines",
            text: "Tubes crépinés haute pression pour captage d'eau, pièges à résine et cartouches de clarification.",
            to: "/ScreenTubesPage",
            badge: "Tubulaire",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
              alt: "Tubes crépinés en acier inoxydable",
            },
          },
          {
            title: "Grilles courbes (Sieve Bend)",
            text: "Panneaux tamisants gravitaires statiques pour amidonnerie, papeterie et récupération minérale.",
            to: "/SieveBendScreensPage",
            badge: "Écoulement gravitaire",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
              alt: "Grille courbe gravitaire",
            },
          },
          {
            title: "Crépines et buses filtrantes",
            text: "Buses à fentes vissées ou à bride pour planchers de filtration, résines échangeuses d'ions et filtres à sable.",
            to: "/FilterNozzlesPage",
            badge: "Planchers crépines",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
              alt: "Buses filtrantes en fil profilé",
            },
          },
        ],
      },
      cta: {
        id: "flat-panel-cta",
        eyebrow: "Panneaux sur mesure",
        heading: "Obtenir une cotation technique pour panneaux plats",
        lead: "Transmettez-nous vos cotes d'encadrement, fente requise et caractéristiques d'exploitation pour recevoir notre proposition chiffrée.",
        secondaryLabel: "Consulter la gamme",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Nuances AISI 304 et 316L avec encadrements mécano-soudés selon vos spécifications.",
      },
    },
  },
  pt: {
    diagramTitle: "Estrutura plana soldada por resistência",
    diagramLead: "Arames contínuos em perfil V soldados por resistência ortogonalmente sobre barras de suporte formam telas planas de elevada rigidez mecânica e ranhuras antientupimento.",
    content: {
      breadcrumbKey: "FlatPanelScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Painel de tela em cunha",
        standardBadge: "Solda por resistência",
        heading: "Painéis planos de tela de arame em cunha",
        lead: "Painéis planos de tela de aço inoxidável fabricados com arames contínuos em perfil V soldados ortogonalmente sobre barras de suporte transversais. Desenvolvidos para resistir a fortes vibrações, proporcionando estabilidade planar e desaguamento contínuo.",
        secondaryCta: {
          label: "Ver telas de arame em cunha",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
          alt: "Painel filtrante plano de tela de aço inoxidável com moldura reforçada",
        },
        cardNote: "Estrutura planar soldada por resistência",
        cardLink: {
          label: "Visão geral da categoria",
          to: "/WedgeWireScreenPage",
        },
        specs: [
          { label: "Largura de fenda", value: "0,02 – 5,0 mm" },
          { label: "Construção", value: "Plana soldada por resistência" },
          { label: "Dimensões padrão", value: "Até 1.500 × 3.000 mm" },
          { label: "Ligas de aço", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Rigidez estrutural e capacidade mecânica",
        heading: "Superfícies planas de alta precisão para desaguamento e separação industrial",
        paragraphs: [
          "Os painéis planos de tela em cunha MutualFilter são produzidos pela soldagem por eletrofusão em cada ponto de cruzamento entre os arames de superfície em V e as barras de suporte. Esse arranjo ortogonal rígido cria uma plataforma indeformável capaz de suportar grandes volumes de polpa, elevadas forças G em peneiras vibratórias e impactos de alimentação contínua.",
          "O perfil em cunha alarga-se em direção ao interior, garantindo que as partículas menores ou iguais à abertura atravessem a tela sem atrito lateral. O material retido corre livremente pela superfície superior, evitando o bloqueio mecânico e garantindo operação ininterrupta.",
          "A estrutura perimetral, tirantes de reforço e flanges de fixação são adaptados para montagem direta em peneiras vibratórias, calhas estáticas e caixas de drenagem. Os painéis podem ser fornecidos com barras chatas soldadas, cantoneiras furadas ou perfis de borracha amortecedora.",
        ],
        keyPoints: [
          "Construção ortogonal soldada que mantém a abertura de fenda constante mesmo sob vibração contínua e carga pesada.",
          "Abertura em perfil V alargada para o interior previne o entupimento de partículas e assegura fluxo desobstruído.",
          "Bordas e molduras customizadas com barras chatas, cantoneiras flangeadas e barras de fixação lateral.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
          alt: "Fabricação de painéis planos de tela de arame em cunha",
        },
      },
      specTable: {
        heading: "Parâmetros padrão de fabricação de painéis planos",
        intro: "Aberturas de fenda típicas, perfis de arame superficial e espaçamentos de barra de suporte para processos industriais.",
        columns: [
          { key: "slot", label: "Abertura de fenda (mm)", align: "left" },
          { key: "surfaceWire", label: "Perfil de arame superficial", align: "left" },
          { key: "supportRod", label: "Tipo de barra de suporte", align: "left" },
          { key: "rodPitch", label: "Espaçamento de suporte", align: "right" },
          { key: "primaryDuty", label: "Aplicação industrial", align: "left" },
        ],
        rows: [
          {
            slot: "0,05 – 0,20",
            surfaceWire: "Arame fino em V",
            supportRod: "Barra chata retangular",
            rodPitch: "15 – 25 mm",
            primaryDuty: "Desaguamento de finos minerais e amido",
          },
          {
            slot: "0,25 – 0,50",
            surfaceWire: "Arame padrão em V",
            supportRod: "Barra chata de alto perfil",
            rodPitch: "20 – 30 mm",
            primaryDuty: "Processamento de carvão, celulose, efluentes",
          },
          {
            slot: "0,75 – 1,50",
            surfaceWire: "Arame reforçado em V",
            supportRod: "Barra estrutural reforçada",
            rodPitch: "25 – 40 mm",
            primaryDuty: "Classificação de agregados, usinas de açúcar",
          },
          {
            slot: "2,00 – 5,00",
            surfaceWire: "Arame de grande seção",
            supportRod: "Viga de suporte pesado",
            rodPitch: "30 – 50 mm",
            primaryDuty: "Separação de materiais grosseiros e grades",
          },
        ],
        caption: "Disponível em aços inoxidáveis AISI 304 e 316L. Fendas customizadas entre 0,02 mm e 5,0 mm sob encomenda.",
      },
      features: {
        heading: "Atributos construtivos e vantagens",
        intro: "Vantagens estruturais das telas planas soldadas em comparação a telas de arame trançado e chapas perfuradas.",
        items: [
          {
            title: "Perfil V autolimpante",
            text: "O alívio traseiro imediato reduz o atrito e impede o aprisionamento de partículas na abertura da fenda.",
          },
          {
            title: "Resistência a vibrações severas",
            text: "A eletrosoldagem de cada junção confere grande resistência à fadiga em peneiras mecânicas de alta intensidade.",
          },
          {
            title: "Maior área aberta efetiva",
            text: "Fendas contínuas paralelas oferecem maior capacidade de escoamento em relação a chapas perfuradas equivalentes.",
          },
          {
            title: "Bordas de fixação versáteis",
            text: "Opções de montagem com barras de fixação lateral, cantoneiras perfuradas ou guarnições de borracha para encaixe rápido.",
          },
        ],
      },
      related: {
        heading: "Outras configurações de tela em cunha",
        intro: "Conheça também formatos curvos, cilíndricos e tubulares fabricados com perfis em V soldados.",
        cards: [
          {
            title: "Telas de arame em cunha",
            text: "Visão abrangente de todos os perfis em V, orientações de ranhuras e formatos de separação industrial.",
            to: "/WedgeWireScreenPage",
            badge: "Família de produto",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp",
              alt: "Visão geral de telas de arame em cunha",
            },
          },
          {
            title: "Painéis curvos",
            text: "Telas em arco e painéis com raio curvado operando por escoamento hidráulico tangencial contínuo.",
            to: "/CurvedScreensPage",
            badge: "Formatos radiais",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
              alt: "Painel curvo de tela em cunha",
            },
          },
          {
            title: "Cilindros de tela",
            text: "Tambores rotativos e cilindros de fluxo interno ou externo com fendas axiais ou circunferenciais.",
            to: "/CylindricalScreensPage",
            badge: "Cilindros",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Cilindros de tela de arame em cunha",
            },
          },
          {
            title: "Tubos de tela",
            text: "Tubos de alta resistência para poços artesianos, retenção de resinas e elementos filtrantes industriais.",
            to: "/ScreenTubesPage",
            badge: "Tubulares",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
              alt: "Tubos de tela de aço inoxidável",
            },
          },
          {
            title: "Peneiras de arco (Sieve Bend)",
            text: "Telas estáticas por gravidade para desaguamento em amido, papel e celulose e lavagem de carvão.",
            to: "/SieveBendScreensPage",
            badge: "Alimentação gravitacional",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
              alt: "Peneira de arco estática",
            },
          },
          {
            title: "Bocais e bicos filtrantes",
            text: "Bicos ranhurados rosqueados ou flangeados para fundos falsos de drenagem, leitos de areia e colunas de resina.",
            to: "/FilterNozzlesPage",
            badge: "Fundos de drenagem",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
              alt: "Bicos filtrantes de tela de arame em cunha",
            },
          },
        ],
      },
      cta: {
        id: "flat-panel-cta",
        eyebrow: "Painéis sob medida",
        heading: "Solicite um orçamento para painéis planos de tela em cunha",
        lead: "Informe as dimensões do seu equipamento, a abertura de fenda desejada e as condições de processo para receber uma especificação técnica detalhada.",
        secondaryLabel: "Ver linha de telas",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Produzidos em aços inoxidáveis AISI 304 e 316L com estruturas e molduras customizadas.",
      },
    },
  },
};
